import { uiText, useUiLanguage } from '@/i18n/interface';
import React, { useState } from 'react';
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { Badge } from "@/components/ui/badge";
import { Trash2 } from 'lucide-react';
import { PaymentCategory } from '@/types/payment';
import { getCurrencySymbol, formatCurrencyAmount } from '@/utils/currencyUtils';
import { AVAILABLE_CURRENCIES } from '@/utils/currencyUtils';
import { useToast } from "@/hooks/use-toast";
import { supabase } from '@/integrations/supabase/client';
import { useCurrencySettings } from '@/hooks/useCurrencySettings';

interface PaymentMethod {
  id: string;
  name: string;
  label: string;
}

interface PaymentSettings {
  availableCurrencies: string[];
}

interface CategoryCardProps {
  category: PaymentCategory;
  settings: PaymentSettings;
  onUpdateCategory: (categoryId: string, field: string, value: any) => void;
  onRemoveCategory: (categoryId: string) => void;
}

const CategoryCard: React.FC<CategoryCardProps> = ({
  category,
  settings,
  onUpdateCategory,
  onRemoveCategory
}) => {
  useUiLanguage();
  const { toast } = useToast();
  const { defaultCurrency } = useCurrencySettings();
  // Use the category's currency or fall back to CHF default
  const displayCurrency = category.currency || defaultCurrency;
  const currencySymbol = getCurrencySymbol(displayCurrency);
  const availablePaymentMethods = [
    { id: 'bank_transfer', name: 'bank_transfer', label: uiText("Bank Transfer") },
    { id: 'credit_card', name: 'credit_card', label: uiText("Credit Card") },
    { id: 'cash', name: 'cash', label: uiText("Cash") },
    { id: 'paypal', name: 'paypal', label: uiText("PayPal") }
  ];

  const handlePaymentMethodChange = (methodId: string, checked: boolean) => {
    let updatedMethods = [...(category.paymentMethods || ['bank_transfer'])];
    if (checked) {
      if (!updatedMethods.includes(methodId)) {
        updatedMethods.push(methodId);
      }
    } else {
      updatedMethods = updatedMethods.filter(m => m !== methodId);
    }
    onUpdateCategory(category.id, 'paymentMethods', updatedMethods);
  };

  const handleDeleteClick = () => {
    if (window.confirm(uiText("Are you sure you want to delete the \"{{value0}}\" category? This action cannot be undone.", { value0: category.name }))) {
      onRemoveCategory(category.id);
    }
  };

  const handleToggleStripeIntegration = (enabled: boolean) => {
    onUpdateCategory(category.id, 'stripeIntegrationEnabled', enabled);
  };

  return (
    <div className="wh-pricing-category">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <h4 className="font-medium text-lg">{category.name}</h4>
          <Badge variant="secondary">{displayCurrency}</Badge>
          {category.isActive === false && <Badge variant="destructive">{uiText("Inactive")}</Badge>}
          {category.stripeIntegrationEnabled && <Badge variant="default">{uiText("Stripe Enabled")}</Badge>}
        </div>
        <Button
          variant="destructive"
          size="sm"
          onClick={handleDeleteClick}
          title={uiText("Delete category")}
        >
          <Trash2 className="h-4 w-4" />
        </Button>
      </div>

<div className="wh-pricing-grid">
  <div>
    <Label htmlFor={`${category.id}-1year`}>{uiText("1 Year Subscription (")}{currencySymbol})</Label>
    <Input
      id={`${category.id}-1year`}
      type="number"
      step="0.01"
      min="0"
      max="1000000000"
      value={category.amounts.oneYear.toFixed(2)}
      onChange={(e) => onUpdateCategory(category.id, 'oneYear', parseFloat(e.target.value) || 0)}
      className="mt-1"
    />
  </div>
  <div>
    <Label htmlFor={`${category.id}-6months`}>{uiText("6 Months Subscription (")}{currencySymbol})</Label>
    <Input
      id={`${category.id}-6months`}
      type="number"
      step="0.01"
      min="0"
      max="1000000000"
      value={category.amounts.sixMonths.toFixed(2)}
      onChange={(e) => onUpdateCategory(category.id, 'sixMonths', parseFloat(e.target.value) || 0)}
      className="mt-1"
    />
  </div>
  <div>
    <Label htmlFor={`${category.id}-3months`}>{uiText("3 Months Subscription (")}{currencySymbol})</Label>
    <Input
      id={`${category.id}-3months`}
      type="number"
      step="0.01"
      min="0"
      max="1000000000"
      value={category.amounts.threeMonths.toFixed(2)}
      onChange={(e) => onUpdateCategory(category.id, 'threeMonths', parseFloat(e.target.value) || 0)}
      className="mt-1"
    />
  </div>
  <div>
    <Label htmlFor={`${category.id}-monthly`}>{uiText("Monthly Subscription (")}{currencySymbol})</Label>
    <Input
      id={`${category.id}-monthly`}
      type="number"
      step="0.01"
      min="0"
      max="1000000000"
      value={category.amounts.monthly.toFixed(2)}
      onChange={(e) => onUpdateCategory(category.id, 'monthly', parseFloat(e.target.value) || 0)}
      className="mt-1"
    />
  </div>
</div>
      <div className="mb-4">
        <Label>{uiText("Currency")}</Label>
        <Select
          value={displayCurrency}
          onValueChange={(value) => onUpdateCategory(category.id, 'currency', value)}
        >
          <SelectTrigger className="w-48 mt-1">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {AVAILABLE_CURRENCIES.map(currency => (
              <SelectItem key={currency.code} value={currency.code}>
                {currency.symbol} {currency.code} - {uiText(currency.name)}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <div className="mb-4">
        <Label>{uiText("Payment Methods")}</Label>
        <div className="grid grid-cols-2 gap-2 mt-2">
          {availablePaymentMethods.map(method => (
            <div key={method.id} className="flex items-center space-x-2">
              <Checkbox
                id={`${category.id}-${method.id}`}
                checked={(category.paymentMethods || ['bank_transfer']).includes(method.id)}
                onCheckedChange={(checked) => handlePaymentMethodChange(method.id, !!checked)}
              />
              <label htmlFor={`${category.id}-${method.id}`} className="text-sm">{method.label}</label>
            </div>
          ))}
        </div>
      </div>

      <div className="mb-4">
        <Label>{uiText("Stripe Integration")}</Label>
        <div className="space-y-2 mt-2">
          <div className="flex items-center space-x-2">
            <Checkbox
              id={`${category.id}-stripe`}
              checked={category.stripeIntegrationEnabled || false}
              onCheckedChange={handleToggleStripeIntegration}
            />
            <label htmlFor={`${category.id}-stripe`} className="text-sm">
              {uiText("Enable Stripe payment processing")}</label>
          </div>

          {category.stripeIntegrationEnabled && (
            <div className="ml-6 space-y-2">
              <Label htmlFor={`${category.id}-stripe-link`}>{uiText("Stripe Payment Link")}</Label>
              <Input
                id={`${category.id}-stripe-link`}
                type="url"
                value={category.stripeLink || ''}
                onChange={(e) => onUpdateCategory(category.id, 'stripeLink', e.target.value)}
                placeholder="https://buy.stripe.com/..."
                className="w-full"
              />
            </div>
          )}
        </div>
      </div>

      <div className="text-sm text-gray-600 dark:text-gray-400">
        <p><strong>{uiText("Payment Options:")}</strong></p>
        {category.name === 'Free of Charge' ? (
          <p>{uiText("• No payment required - completely free!")}</p>
        ) : (
          <>
            <p>{uiText("• Pay at once: Full amount")}</p>
            <p>{uiText("• 2 Installments:")}{' '}{currencySymbol}{(category.amounts.oneYear / 2).toFixed(2)} {' '}{uiText("each (1 year) |")}{' '}{currencySymbol}{(category.amounts.sixMonths / 2).toFixed(2)} {' '}{uiText("each (6 months)")}</p>
            <p>{uiText("• 3 Installments:")}{' '}{currencySymbol}{(category.amounts.oneYear / 3).toFixed(2)} {' '}{uiText("each (1 year) |")}{' '}{currencySymbol}{(category.amounts.sixMonths / 3).toFixed(2)} {' '}{uiText("each (6 months)")}</p>
            {category.stripeIntegrationEnabled && (
              <p className="mt-2 text-green-600">{uiText("• Stripe payments enabled - students can pay online!")}</p>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default CategoryCard;
